import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTimer } from "@/context/TimerContext";
import { Button } from "@/components/ui/button";
import { Play, Pause, Square, CheckCircle2, Sunrise } from "lucide-react";
import { toast } from "sonner";

const PRIORITY_DOT = {
  high: "bg-rose-500",
  medium: "bg-amber-500",
  low: "bg-sky-500",
};

const STATUS_LABEL = {
  todo: "To do",
  in_progress: "In progress",
};

export default function MyDay() {
  const { user } = useAuth();
  const { timer, start: startTimer, pause, resume, stop } = useTimer();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const [tRes, pRes, cRes] = await Promise.all([
        api.get(`/tasks?assignee_id=${user.id}`),
        api.get("/projects?include_hidden=true"),
        api.get("/clients"),
      ]);
      // Only show todo + in_progress
      const filtered = (tRes.data || []).filter(t =>
        t.status === "todo" || t.status === "in_progress"
      );
      // Sort: in_progress first, then by priority (high > medium > low), then by due date asc
      const prioRank = { high: 0, medium: 1, low: 2 };
      filtered.sort((a, b) => {
        if (a.status !== b.status) return a.status === "in_progress" ? -1 : 1;
        const p = (prioRank[a.priority] ?? 3) - (prioRank[b.priority] ?? 3);
        if (p !== 0) return p;
        return (a.due_date || "9999-99-99").localeCompare(b.due_date || "9999-99-99");
      });
      setTasks(filtered);
      setProjects(pRes.data || []);
      setClients(cRes.data || []);
    } catch (e) { console.warn("[MyDay] load failed", e); }
    setLoading(false);
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const projectById = Object.fromEntries(projects.map(p => [p.id, p]));
  const clientById = Object.fromEntries(clients.map(c => [c.id, c]));

  const parentInfo = (t) => {
    const p = projectById[t.project_id];
    if (!p) return { task: "—", client: "" };
    const c = clientById[p.client_id];
    return { task: p.name, client: c?.company || c?.name || "" };
  };

  const isProjectActive = (t) => {
    const p = projectById[t.project_id];
    return p?.status === "active" && !p?.archived;
  };

  const doStart = async (t) => {
    try {
      await startTimer({ project_id: t.project_id, task_id: t.id, description: t.title });
      load(); // timer start flips todo → in_progress server-side
    } catch (e) { console.warn("[MyDay] start failed", e); /* toast handled in context */ }
  };

  const markDone = async (t) => {
    try {
      // If this task's timer is running, stop it first
      if (timer && timer.task_id === t.id) await stop();
      await api.put(`/tasks/${t.id}`, { status: "done" });
      toast.success("Marked done");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  if (loading) return null;
  if (tasks.length === 0) {
    return (
      <div className="border border-neutral-200 bg-white p-6 mb-6" data-testid="my-day-widget">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-semibold mb-2">
          <Sunrise size={14} /> My day
        </div>
        <div className="font-display text-lg font-bold mb-1">All clear</div>
        <div className="text-sm text-neutral-500">You have no todo or in-progress activities. Ask an admin to assign you something, or check the <Link to="/tasks" className="underline">Activities</Link> tab.</div>
      </div>
    );
  }

  return (
    <div className="border border-neutral-200 bg-gradient-to-br from-amber-50 to-white overflow-hidden mb-6" data-testid="my-day-widget">
      <div className="px-5 py-3 border-b border-amber-200 flex items-center justify-between bg-gradient-to-r from-amber-100 to-transparent">
        <div className="flex items-center gap-2">
          <Sunrise size={14} className="text-amber-700" />
          <div className="text-[10px] uppercase tracking-[0.2em] text-amber-800 font-semibold">My day</div>
          <div className="text-xs text-neutral-500">· {tasks.length} activit{tasks.length === 1 ? "y" : "ies"} to work on</div>
        </div>
      </div>
      <ul className="divide-y divide-neutral-100">
        {tasks.map(t => {
          const parent = parentInfo(t);
          const active = timer && timer.task_id === t.id;
          const paused = active && timer.is_paused;
          const disabled = !isProjectActive(t);
          return (
            <li key={t.id} className="flex items-center gap-3 px-5 py-3 hover:bg-white transition-colors" data-testid={`my-day-row-${t.id}`}>
              <span className={`w-1.5 h-6 rounded-sm shrink-0 ${PRIORITY_DOT[t.priority] || "bg-neutral-300"}`} title={`${t.priority} priority`} />
              <div className="flex-1 min-w-0">
                <Link to={`/projects/${t.project_id}`} className="block text-sm font-semibold text-neutral-900 hover:underline truncate">
                  {t.title}
                </Link>
                <div className="text-[11px] text-neutral-500 flex items-center gap-1.5 truncate">
                  <span className="uppercase tracking-widest">{STATUS_LABEL[t.status]}</span>
                  <span>·</span>
                  <span className="truncate">{parent.task}</span>
                  {parent.client && (<><span>·</span><span className="truncate">{parent.client}</span></>)}
                  {t.due_date && (<><span>·</span><span className="font-mono">Due {t.due_date}</span></>)}
                </div>
              </div>
              {active ? (
                <div className="flex items-center gap-1 shrink-0">
                  {paused ? (
                    <Button size="sm" onClick={resume} className="rounded-sm bg-amber-600 hover:bg-amber-700 text-white h-8 text-xs" data-testid={`my-day-resume-${t.id}`}>
                      <Play size={12} className="mr-1" /> Resume
                    </Button>
                  ) : (
                    <Button size="sm" onClick={pause} className="rounded-sm bg-neutral-700 hover:bg-neutral-600 text-white h-8 text-xs" data-testid={`my-day-pause-${t.id}`}>
                      <Pause size={12} className="mr-1" /> Pause
                    </Button>
                  )}
                  <Button size="sm" onClick={async () => { await stop(); load(); }} className="rounded-sm bg-rose-600 hover:bg-rose-700 text-white h-8 text-xs" data-testid={`my-day-stop-${t.id}`}>
                    <Square size={12} />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    size="sm"
                    onClick={() => doStart(t)}
                    disabled={disabled}
                    className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs disabled:opacity-40"
                    data-testid={`my-day-start-${t.id}`}
                    title={disabled ? "Parent task isn't Active — can't log time" : "Start working on this"}
                  >
                    <Play size={12} className="mr-1" /> Start work
                  </Button>
                  {isAdmin && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => markDone(t)}
                      className="rounded-sm h-8 w-8 p-0 text-emerald-700 hover:bg-emerald-50 border-emerald-200"
                      data-testid={`my-day-done-${t.id}`}
                      title="Mark done"
                    >
                      <CheckCircle2 size={14} />
                    </Button>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
